import type { RunDetailPoint } from './types';
import { config } from '../config';
import { M_PER_MILE, M_PER_KM } from './format';

export interface Split {
  index: number; // 1-based
  distance_m: number; // cumulative distance at end of split
  duration_s: number; // seconds for this split
  pace_s_per_unit: number; // seconds per mile or km
  avg_hr?: number;
}

export function computeSplits(points: RunDetailPoint[], unit: 'mi' | 'km'): Split[] {
  if (points.length < 2) return [];
  const step = unit === 'mi' ? M_PER_MILE : M_PER_KM;

  const splits: Split[] = [];
  let splitStartIdx = 0;
  let nextBoundary = step;
  let splitHrSum = 0;
  let splitHrCount = 0;

  const pushSplit = (endIdx: number, boundaryDistance: number) => {
    const start = points[splitStartIdx];
    const end = points[endIdx];
    const dur = Math.max(1, end.t - start.t);
    const dist = Math.max(1, end.d - start.d);
    const pace = (dur / dist) * step;
    splits.push({
      index: splits.length + 1,
      distance_m: boundaryDistance,
      duration_s: Math.round(dur),
      pace_s_per_unit: Math.round(pace),
      avg_hr: splitHrCount > 0 ? Math.round(splitHrSum / splitHrCount) : undefined
    });
  };

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (typeof p.hr === 'number') {
      splitHrSum += p.hr;
      splitHrCount += 1;
    }
    while (p.d >= nextBoundary && i > splitStartIdx) {
      pushSplit(i, nextBoundary);
      splitStartIdx = i;
      nextBoundary += step;
      splitHrSum = 0;
      splitHrCount = 0;
    }
  }

  // Final partial split
  const last = points[points.length - 1];
  if (last.d > (splits.at(-1)?.distance_m ?? 0) + 10) {
    pushSplit(points.length - 1, last.d);
  }

  return splits;
}

/**
 * Smoothed pace series using a rolling window of ~0.1 unit (mi or km).
 * Returns pace in seconds per unit at each point (for charting).
 */
export function computePaceSeries(points: RunDetailPoint[], unit: 'mi' | 'km'): (number | null)[] {
  const step = unit === 'mi' ? M_PER_MILE : M_PER_KM;
  const windowM = step * 0.1; // ~0.1 mi or 0.1 km window
  const out: (number | null)[] = new Array(points.length).fill(null);

  let k = 0;
  for (let i = 0; i < points.length; i++) {
    while (points[i].d - points[k].d > windowM && k < i) k++;
    if (k >= i) continue;
    const dt = points[i].t - points[k].t;
    const dd = points[i].d - points[k].d;
    if (dt <= 0 || dd <= 0) continue;
    const pace = (dt / dd) * step; // seconds per unit
    // Clamp absurd values (GPS jitter at stops)
    if (pace > 0 && pace < 60 * 60) {
      out[i] = pace;
    }
  }
  return out;
}

export interface HrZoneBucket {
  zone: number; // 1-based
  label: string;
  lower: number; // bpm
  upper: number; // bpm
  seconds: number;
}

export function computeHrZones(points: RunDetailPoint[]): HrZoneBucket[] | null {
  const zonesCfg = config.hrZones;
  if (!zonesCfg || zonesCfg.length === 0) return null;

  const buckets: HrZoneBucket[] = zonesCfg.map((z, i) => ({
    zone: i + 1,
    label: z.label,
    lower: z.lower,
    upper: z.upper,
    seconds: 0
  }));

  let hasHr = false;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const dt = Math.max(0, curr.t - prev.t);
    if (dt === 0) continue;
    const hr = curr.hr ?? prev.hr;
    if (typeof hr !== 'number') continue;
    hasHr = true;
    const zoneIdx = pickZone(hr, buckets);
    buckets[zoneIdx].seconds += dt;
  }

  if (!hasHr) return null;
  return buckets;
}

function pickZone(hr: number, buckets: HrZoneBucket[]): number {
  // Below lowest zone's upper → Z1; above highest zone's upper → top zone.
  if (hr <= buckets[0].upper) return 0;
  for (let z = 1; z < buckets.length - 1; z++) {
    if (hr <= buckets[z].upper) return z;
  }
  return buckets.length - 1;
}
